import React, { FC, useMemo } from "react";
import { WorkspaceAssignment } from "@common/types/workspace";
import {
    AvatarGroup,
    type AvatarGroupSize,
} from "@design-system/Avatar/AvatarGroup";
import { HStack } from "@design-system/HStack/HStack";
import { WorkspacePendingInvite } from "@common/dto/workspace/workspace-pending-invite.dto";
import { WORKSPACE_ROLES_LABELS } from "@common/constants";
import { PendingInviteIcon } from "./PendingInviteIcon";
import { TeamMemberIcon } from "./TeamMemberIcon";

interface TeamListProps {
    team: WorkspaceAssignment[];
    invites?: WorkspacePendingInvite[];
    appendContent?: React.ReactNode;
    iconSize?: AvatarGroupSize;
}

type TeamListItem = WorkspaceAssignment | WorkspacePendingInvite;

function isWorkspaceAssignment(
    item: TeamListItem,
): item is WorkspaceAssignment {
    return "user" in item;
}

const getKey = (item: TeamListItem) =>
    isWorkspaceAssignment(item) ? `user-${item.user.id}` : `invite-${item.id}`;

const getLabel = (item: TeamListItem) =>
    isWorkspaceAssignment(item)
        ? `${item.user.name} (${WORKSPACE_ROLES_LABELS[item.role]})`
        : `Pending: ${item.email} (${WORKSPACE_ROLES_LABELS[item.role]})`;

const renderAvatar = (item: TeamListItem) =>
    isWorkspaceAssignment(item) ? (
        <TeamMemberIcon
            name={item.user.name}
            role={item.role}
            email={item.user.email}
            iconColor={item.user.iconColor}
            avatar={item.user.avatar}
        />
    ) : (
        <PendingInviteIcon invite={item} />
    );

export const TeamList: FC<TeamListProps> = ({
    team,
    invites,
    appendContent,
    iconSize = "xs",
}) => {
    const items = useMemo(() => [...team, ...(invites ?? [])], [team, invites]);

    return (
        <HStack align="center">
            <AvatarGroup
                items={items}
                getKey={getKey}
                getLabel={getLabel}
                renderAvatar={renderAvatar}
                max={5}
                size={iconSize}
            />
            {appendContent}
        </HStack>
    );
};
